import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import { companyInfo } from '../data/siteData';

export default function StoejhegnPage() {
  return (
    <>
      <SEO
        title="Støjhegn til haven | Hold støjen væk fra boligen | Hovendals"
        description="Med støjhegn får du mere ro i haven og på din grund. Hovendals rådgiver om støjdæmpende hegn, placering og pris. Få et uforpligtende tilbud."
        ogImage="/_assets/hovendals-logo.webp"
      />

      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-[#F3F4F7] border-b border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Afskærmning & Ro
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#143463] tracking-tight leading-[1.1]">
              Støjhegn
            </h1>
            <p className="text-lg sm:text-xl text-[#4B515D] font-light leading-relaxed">
              Skab mere ro og afskærmning med en løsning, der passer til dine omgivelser.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <span>Indhent tilbud</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white border border-[#D6D3CE] text-[#143463] hover:text-[#004FA4] font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <i className="fa-sharp fa-solid fa-phone text-[#004FA4]" aria-hidden="true"></i>
                <span>Ring {companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1 & 2: Introduktion og Anvendelsesområder */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Introduktion
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
                En gennemtænkt løsning mod støj og indblik
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  Trafik, naboaktivitet eller støj fra nærliggende veje kan gøre det svært at nyde haven eller terrassen i fulde drag. Med et professionelt etableret støjhegn kan du skabe en markant mere uforstyrret atmosfære omkring din bolig eller erhvervsejendom.
                </p>
                <p>
                  Hos HOVENDALS tager vi udgangspunkt i de konkrete fysiske forhold på din grund. Vi måler op og vurderer terræn, højdebehov og placering, så løsningen både yder den ønskede afskærmning og integreres æstetisk i grundens helhed.
                </p>
                <p>
                  Vi leverer og monterer løsninger for både private haveejere og virksomheder i hele Danmark.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Anvendelsesområder
              </span>
              <h3 className="text-2xl sm:text-3xl font-light text-[#143463] tracking-tight">
                Hvor anvendes støjhegn?
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-6 bg-[#F3F4F7] border border-[#D6D3CE]/60">
                  <div className="w-9 h-9 bg-white text-[#004FA4] flex items-center justify-center text-sm mb-3">
                    <i className="fa-sharp fa-solid fa-house" aria-hidden="true"></i>
                  </div>
                  <h4 className="font-medium text-[#143463] text-base mb-1">Private haver & terrasser</h4>
                  <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                    Afskærmning mod vej, sti eller tilstødende naboområder med ønske om mere privatliv og ro.
                  </p>
                </div>

                <div className="p-6 bg-[#F3F4F7] border border-[#D6D3CE]/60">
                  <div className="w-9 h-9 bg-white text-[#004FA4] flex items-center justify-center text-sm mb-3">
                    <i className="fa-sharp fa-solid fa-car" aria-hidden="true"></i>
                  </div>
                  <h4 className="font-medium text-[#143463] text-base mb-1">Vej- og indkørselsarealer</h4>
                  <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                    Effektiv reduktion af generende støj fra forbipasserende biler og cykelstier.
                  </p>
                </div>

                <div className="p-6 bg-[#F3F4F7] border border-[#D6D3CE]/60">
                  <div className="w-9 h-9 bg-white text-[#004FA4] flex items-center justify-center text-sm mb-3">
                    <i className="fa-sharp fa-solid fa-building" aria-hidden="true"></i>
                  </div>
                  <h4 className="font-medium text-[#143463] text-base mb-1">Erhvervsgrunde & ramper</h4>
                  <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                    Afskærmning omkring læsseramper, parkeringspladser eller produktionsområder.
                  </p>
                </div>

                <div className="p-6 bg-[#F3F4F7] border border-[#D6D3CE]/60">
                  <div className="w-9 h-9 bg-white text-[#004FA4] flex items-center justify-center text-sm mb-3">
                    <i className="fa-sharp fa-solid fa-layer-group" aria-hidden="true"></i>
                  </div>
                  <h4 className="font-medium text-[#143463] text-base mb-1">Fællesarealer & boligforeninger</h4>
                  <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                    Struktureret afgrænsning af opholdsarealer, affaldsstationer og parkeringsanlæg.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 3: Løsninger & Proces */}
      <section className="py-20 md:py-28 bg-[#F3F4F7] border-t border-b border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Vores Proces
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight mt-2">
              Fra opmåling til færdigmonteret støjhegn
            </h2>
            <p className="text-[#4B515D] font-light text-base mt-3">
              Vi sikrer, at dit hegn opbygges på et solidt fundament med holdbare stolper og materialer egnet til det danske vejrlig.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-white border border-[#D6D3CE]/60">
              <span className="text-2xl font-light text-[#004FA4] block mb-3">01</span>
              <h3 className="text-lg font-medium text-[#143463] mb-2">Grundig opmåling & rådgivning</h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Vi gennemgår opgaven sammen med dig, måler strækningen og drøfter dine ønsker til højde, udtryk og afskærmningseffekt.
              </p>
            </div>

            <div className="p-8 bg-white border border-[#D6D3CE]/60">
              <span className="text-2xl font-light text-[#004FA4] block mb-3">02</span>
              <h3 className="text-lg font-medium text-[#143463] mb-2">Fremstilling på mål</h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Elementerne dimensioneres og forberedes, så modulerne passer præcist til grundens fald og linjeføring.
              </p>
            </div>

            <div className="p-8 bg-white border border-[#D6D3CE]/60">
              <span className="text-2xl font-light text-[#004FA4] block mb-3">03</span>
              <h3 className="text-lg font-medium text-[#143463] mb-2">Professionel montering</h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Vores erfarne montører opsætter hegnet sikkert i jorden og sikrer, at konstruktionen står stabilt i mange år frem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 4: Kontakt */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#143463] text-white p-10 sm:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl space-y-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF]">
                Uforpligtende dialog
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-white tracking-tight">
                Vil du høre mere om mulighederne for støjhegn?
              </h2>
              <p className="text-sm sm:text-base text-slate-300 font-light leading-relaxed">
                Kontakt Simon Hovendal for en drøftelse af dit projekt og få et uforpligtende tilbud tilpasset dine mål.
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row gap-3">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
              >
                Indhent tilbud
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-8 py-4 bg-transparent border border-white/40 hover:border-white text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
              >
                Ring {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <QuoteSection />
    </>
  );
}
